// ============================================
// IMU calibration protocol (firmware `calibrate`, v0.5.43+)
// ============================================
//
// Runs inside IDLE (no mode enter/exit): `calibrate` begins and enters step 1,
// `calabort` stops and restores the sensor. All commands are plain text + \n;
// events are NDJSON, one object per line — parse with `parseFactoryLine`
// (utils/factoryTest.js), then fold with `reduceCalibrationState`.
//
// Commands: calibrate (start) · calnext (advance from a prompt) · calstatus
//   (resync snapshot) · calsave (persist result) · calabort (cancel).
// Events: start · prompt · collecting · result · saved · aborted · error ·
//   cal_status. Guaranteed (never dropped): start, prompt, result, saved,
//   aborted, error, and the FIRST collecting of each step (the calnext ack,
//   done≈0). Intermediate `collecting` progress is best-effort — decoration only.

import { t } from "i18n";

// `label` / `instruction` are getters so they are translated at read time.
const calStep = (slug) => ({
  slug,
  get label() {
    return t(`utils.calibration.steps.${slug}.label`);
  },
  get instruction() {
    return t(`utils.calibration.steps.${slug}.instruction`);
  },
});

/** The 7 calibration steps, in order. `label` is the short checklist tag;
 *  `instruction` is the operator-friendly positioning text (we show this rather
 *  than the raw firmware `q`, which mentions the `calnext` command). */
export const CALIBRATION_STEPS = [
  calStep("gyro_rest"),
  calStep("xp"),
  calStep("yp"),
  calStep("zp"),
  calStep("xn"),
  calStep("yn"),
  calStep("zn"),
];

export const CAL_SLUGS = CALIBRATION_STEPS.map((s) => s.slug);

// Target for each positioning step: which accel axis (0=X,1=Y,2=Z) should read
// ±1 g. gyro_rest has no single target axis (just flat + still).
export const CAL_TARGETS = {
  xp: { axis: 0, sign: 1 },
  yp: { axis: 1, sign: 1 },
  zp: { axis: 2, sign: 1 },
  xn: { axis: 0, sign: -1 },
  yn: { axis: 1, sign: -1 },
  zn: { axis: 2, sign: -1 },
};

export const TARGET_MG = 1000; // 1 g

/**
 * Advisory "in position" check for the live gauge (not a gate — calnext works at
 * any point in a WAIT step). True when the axis under test reads near ±1 g with
 * the correct sign AND the other two axes are near flat. Returns null for
 * gyro_rest (no single axis). `mg` is [x,y,z] in milli-g.
 */
export function calInPosition(step, mg, { near = 90, flat = 180 } = {}) {
  const target = CAL_TARGETS[step];
  if (!target || !Array.isArray(mg)) return null;
  const onTarget = Math.abs(mg[target.axis] - target.sign * TARGET_MG) < near;
  const offAxesFlat = mg.every(
    (v, i) => i === target.axis || Math.abs(v) < flat
  );
  return onTarget && offAxesFlat;
}

const instructionFor = (slug) =>
  CALIBRATION_STEPS.find((s) => s.slug === slug)?.instruction ?? "";

/** 1-based index of a step slug in the run (0 if unknown). */
export const calStepIndex = (slug) => CAL_SLUGS.indexOf(slug) + 1;

/** Fresh, empty calibration state. */
export function initCalibrationState() {
  return {
    started: false,
    fw: null,
    steps: 7,
    samples: null,
    prompt: null, // { step, index, total, q, expect }
    collecting: null, // { step, done, total }
    result: null, // { valid, gyroBiasMdps, accelScalePpm, accelBiasUg }
    saved: null, // { ok }
    aborted: false,
    error: null, // { reason }
    persisted: null, // whether a calibration is already saved (from cal_status)
    status: null, // raw cal_status snapshot
  };
}

/**
 * Fold one parsed calibration event into the running state (pure). Designed for
 * `events.reduce(reduceCalibrationState, prev)`.
 */
export function reduceCalibrationState(state, ev) {
  if (!ev || !ev.t) return state;

  switch (ev.t) {
    case "start":
      if (ev.mode && ev.mode !== "calibration") return state;
      return {
        ...initCalibrationState(),
        started: true,
        fw: ev.fw ?? null,
        steps: ev.steps ?? 7,
        samples: ev.samples ?? null,
        persisted: state.persisted, // keep any known persisted flag
      };

    case "prompt":
      return {
        ...state,
        started: true,
        prompt: {
          step: ev.step ?? null,
          index: ev.index ?? null,
          total: ev.total ?? state.steps ?? 7,
          q: ev.q ?? "",
          expect: ev.expect ?? "calnext",
        },
        collecting: null,
      };

    case "collecting":
      return {
        ...state,
        started: true,
        collecting: {
          step: ev.step ?? null,
          done: ev.done ?? 0,
          total: ev.total ?? state.samples ?? null,
        },
        prompt: null,
      };

    case "result":
      return {
        ...state,
        result: {
          valid: ev.valid === true,
          gyroBiasMdps: ev.gyro_bias_mdps ?? null,
          accelScalePpm: ev.accel_scale_ppm ?? null,
          accelBiasUg: ev.accel_bias_ug ?? null,
        },
        prompt: null,
        collecting: null,
      };

    case "saved":
      // v0.5.63 includes the save time; epochS === 0 means the clock wasn't
      // synced at save time (date unknown).
      return {
        ...state,
        saved: { ok: ev.ok === true, epochS: ev.epoch_s ?? null },
      };

    case "aborted":
      return { ...state, aborted: true };

    case "error":
      return { ...state, error: { reason: ev.reason ?? "unknown" } };

    case "cal_status": {
      const persisted = ev.persisted ?? state.persisted;
      const status = {
        step: ev.step ?? null,
        index: ev.index ?? null,
        done: ev.done ?? null,
        valid: ev.valid,
        persisted,
      };
      // Resync: if a run is active and we have no live prompt/collecting yet,
      // seed a positioning prompt from the snapshot so the UI can continue.
      const active = CAL_SLUGS.includes(ev.step);
      if (active && !state.result && !state.prompt && !state.collecting) {
        return {
          ...state,
          started: true,
          persisted,
          status,
          prompt: {
            step: ev.step,
            index: ev.index ?? calStepIndex(ev.step),
            total: state.steps ?? 7,
            q: instructionFor(ev.step),
            expect: "calnext",
          },
        };
      }
      return { ...state, persisted, status };
    }

    default:
      return state;
  }
}
